(ns frontend.ux-overhaul.listing.generic.legacy-views
  "The saved views of the legacy grids, shown in the new listing.

   A legacy view is a `named-view` entity with the key of a legacy grid, for example
   `:oppos-grid`, and no schema version. Its settings are an EDN text with the keys
   `:filters`, `:view-mode`, `:sort-by` and `:sort-direction`, and the columns of
   the grid. The old grid and the new listing read the same entities, so the new
   listing does not write them. It converts the settings when it shows the view, and
   when the user applies it.

   The conversion keeps the view mode, the filters and the sort, with the second
   sort. It does not keep the columns, the column widths, the card settings and the
   grouping of the Kanban board. It drops a filter that the filter bar cannot show,
   and a filter or a sort of a field that the user cannot read in the listing, as the
   search throws for such a field."
  (:require
   [clojure.edn :as edn]
   [clojure.string :as string]
   [common.entity-name :as entity-name]
   [common.schema.registry :as registry]
   [frontend.utils :as fu]))

(def ^:private legacy-view-keys-by-entity-type
  {:entity.type/oppo                         #{:oppos-grid}
   :entity.type/fundraising-opportunity      #{:fundraising-opportunities-grid}
   :entity.type/sales-opportunity            #{:sales-opportunities-grid}
   :entity.type/exit-opportunity             #{:exit-opportunities-grid}
   :entity.type/person                       #{:contacts :persons-grid}
   :entity.type/company                      #{:companies-grid}
   :entity.type/investor                     #{:investors}
   :entity.type/holding                      #{:holdings :holdings-grid}
   :entity.type/calculated-indirect-holding  #{:indirect-holdings}
   :entity.type/fund-commitment              #{:fund-commitments}
   :entity.type/sale-agreement               #{:sale-agreements}
   :entity.type/real-estate                  #{:real-estates-grid}
   :entity.type/fof-asset                    #{:fof-assets-grid}
   :entity.type/other-asset                  #{:other-assets-grid}
   :entity.type/fund                         #{:funds}})

(defn legacy-view-keys
  "The keys of the legacy grids that had the entities of `entity-type`. An entity
   type that has no legacy grid gives the empty set."
  [entity-type]
  (get legacy-view-keys-by-entity-type entity-type #{}))

(defn decode-settings
  "The settings of a legacy view as a map, or nil when they cannot be read. The
   entity has them as an EDN text."
  [settings]
  (cond
    (map? settings) settings
    (string? settings) (try
                         (let [decoded (edn/read-string settings)]
                           (when (map? decoded)
                             decoded))
                         (catch :default _
                           nil))
    :else nil))

(defn- normalize-field-path
  [field]
  (cond
    (keyword? field) [field]
    (sequential? field) (when (seq field) (vec field))
    :else nil))

(defn- filter-value
  "The value that the filter bar has for an option. The facets give
   `\"oppo.status/ok\"` for `:oppo.status/ok`."
  [value]
  (if (keyword? value)
    (if (namespace value)
      (str (namespace value) "/" (name value))
      (name value))
    value))

(defn- values
  "The values of a filter of a legacy view: a set, another collection or one
   value."
  [value]
  (cond
    (nil? value) []
    (set? value) (vec (sort-by str value))
    (sequential? value) (vec value)
    :else [value]))

(defn- with-id
  "A selection as the filter bar makes it. The id is the text of the selection."
  [selection]
  (assoc selection :id (pr-str selection)))

(defn- option-label
  [{:keys [field-path value tenant-schema]}]
  (let [attribute (peek field-path)
        options (:options (when (keyword? attribute)
                            (registry/get-field-schema tenant-schema attribute)))]
    (or (when (and (seq options) (keyword? value))
          (fu/find-option-label options value))
        (when (keyword? value)
          (name value))
        (str value))))

(defn- label-fn
  "The function that gives the label of a value of a filter, as the filter bar shows
   it. Without `tr` the label is the text of the value, and this is enough to tell
   what a filter selects. The labels are for the person who reads the filter bar.
   `entities` is the map of the entities of the session, to give the name of an
   entity. `format-date` gives the text of a date."
  [{:keys [entities tr tenant-schema format-date]}]
  (fn [{:keys [field-path value date?]}]
    (cond
      (not (fn? tr))
      (str value)

      (= value :filter.option/known)
      (tr :filters-editor/is-known)

      (= value :filter.option/unknown)
      (tr :filters-editor/is-unknown)

      (and date? (number? value) (fn? format-date))
      (or (format-date value) (str value))

      (and (number? value) (get entities value))
      (str (entity-name/entity-name nil tr (get entities value)))

      :else
      (option-label {:field-path    field-path
                     :value         value
                     :tenant-schema tenant-schema}))))

(defn- convert-filter
  "A filter of a legacy view as the filter of a field of the filter bar, or nil when
   the filter bar cannot show it."
  [{:keys [field-path spec label]}]
  (let [operand (:operand spec)
        lower (:lower-bound-value spec)
        upper (:upper-bound-value spec)
        value-selection (fn [operator value]
                          {:kind     :value
                           :operator operator
                           :value    (filter-value value)
                           :label    (label {:field-path field-path :value value})})
        date-selection (fn [operator value]
                         (when (number? value)
                           {:kind     :value
                            :operator operator
                            :value    value
                            :label    (label {:field-path field-path :value value :date? true})}))
        existence-selection (fn [operator value]
                              {:kind     :value
                               :operator operator
                               :value    value
                               :label    (label {:field-path field-path :value value})})
        single (fn [selection]
                 (when selection
                   {:mode :single :selections [selection]}))]
    (case operand
      :operand/any-of
      (when-let [selected (seq (values lower))]
        {:mode       :multi
         :selections (mapv #(value-selection :operand/any-of %) selected)})

      ;; Selections of one field are alternatives. A filter that excludes two
      ;; values is not the alternative of two selections, so it is not shown.
      :operand/none-of
      (let [selected (values lower)]
        (when (= 1 (count selected))
          (single (value-selection :operand/none-of (first selected)))))

      :operand/known
      (single (existence-selection :operand/known :filter.option/known))

      :operand/unknown
      (single (existence-selection :operand/unknown :filter.option/unknown))

      :operand/contains
      (when (and (string? lower) (not (string/blank? lower)))
        (single (value-selection :operand/contains lower)))

      :operand/equals
      (when (some? lower)
        (single (value-selection :operand/equals lower)))

      (:operand/smaller-than :operand/greater-than)
      (when (number? lower)
        (single {:kind     :value
                 :operator operand
                 :value    lower
                 :label    (str lower)}))

      (:operand/on-or-after :operand/on-or-before :operand/equals-date)
      (single (date-selection operand lower))

      :operand/between-inclusive-date
      (when (and (number? lower) (number? upper))
        (single {:kind     :value
                 :operator operand
                 :from     lower
                 :to       upper
                 :value    (str lower " .. " upper)
                 :label    (str (label {:field-path field-path :value lower :date? true})
                                " - "
                                (label {:field-path field-path :value upper :date? true}))}))

      :operand/between-inclusive
      (when (and (number? lower) (number? upper))
        (single {:kind     :value
                 :operator operand
                 :from     lower
                 :to       upper
                 :value    (str lower " .. " upper)
                 :label    (str lower " - " upper)}))

      nil)))

(defn- convert-filters
  [{:keys [filters accessible? label]}]
  (->> filters
       (keep (fn [[field spec]]
               (let [field-path (normalize-field-path field)]
                 (when (and field-path
                            (map? spec)
                            (accessible? field-path))
                   (when-let [{:keys [mode selections]} (convert-filter {:field-path field-path
                                                                         :spec       spec
                                                                         :label      label})]
                     [field-path {:field-path field-path
                                  :mode       mode
                                  :selections (mapv with-id selections)}])))))
       (into {})))

(defn- direction
  [value]
  (if (contains? #{"DESC" "desc" :desc} value)
    :desc
    :asc))

(defn- convert-sort
  [{:keys [sort-by sort-direction secondary-sort-by secondary-sort-direction]} accessible?]
  (->> [[sort-by sort-direction]
        [secondary-sort-by secondary-sort-direction]]
       (keep (fn [[field field-direction]]
               (when-let [field-path (normalize-field-path field)]
                 (when (accessible? field-path)
                   {:field-path field-path
                    :direction  (direction field-direction)}))))
       vec))

(def ^:private view-modes
  {:board-mode :kanban
   :grid-mode  :grid})

(defn legacy-view->settings
  "The settings of a view of the new listing, made from the decoded settings
   `settings` of a legacy view: `{:view-mode .. :filters .. :grid {:sorted-by ..}}`.
   A part that the legacy view does not have is not in the result.

   `accessible-field-paths` is the set of the paths of the fields that the user can
   read in the listing. A filter or a sort of another field is dropped. When it is
   nil or empty, no field is dropped. The labels of the filters come from
   `entities`, `tr`, `tenant-schema` and `format-date`. Without `tr` they are the
   text of the values."
  [{:keys [settings accessible-field-paths] :as context}]
  (let [accessible-set (when (seq accessible-field-paths)
                         (into #{} (keep normalize-field-path) accessible-field-paths))
        accessible? (fn [field-path]
                      (or (nil? accessible-set)
                          (contains? accessible-set field-path)))
        label (label-fn context)
        view-mode (get view-modes (:view-mode settings))
        filters (convert-filters {:filters     (:filters settings)
                                  :accessible? accessible?
                                  :label       label})
        sorted-by (convert-sort settings accessible?)]
    (cond-> {}
      view-mode (assoc :view-mode view-mode)
      (seq filters) (assoc :filters filters)
      (seq sorted-by) (assoc :grid {:sorted-by sorted-by}))))
